import { BASE_GRID_OPTIONS, PoGrid } from '@/components/grid/PoGrid';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';
import { GridApi, GridOptions, ICellRendererParams, RowDoubleClickedEvent, SortChangedEvent } from 'ag-grid-community';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { ColDefOrGroup } from '@/lib/ag-grid/types';
import { LocationAssetModel } from '../../types/LocationAssetModel';
import { RelativeDateCellRenderer } from '@/components/grid/cells/RelativeDateCellRenderer';
import { ColumnID } from '@/components/grid/column-ids';
import { PageSortOption, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { LocationAssetSortOption } from '../../api/locations/location.contracts';
import { useTranslation } from '@/lib';
import dayjs from 'dayjs';

interface GuestAssetsAtLocationGridProps {
  data?: LocationAssetModel[];
  isError: boolean;
  noBorder?: boolean;
  isLoading?: boolean;
  onSortChange: (sortOption?: PageSortOption, sortDirection?: PageSortOrder) => void;
  sortOption?: PageSortOption;
  sortDirection?: PageSortOrder;
  onExportToExcelClicked: (gridApi: GridApi<LocationAssetModel> | undefined) => void;
}

export const GuestAssetsAtLocationGrid: FunctionComponent<GuestAssetsAtLocationGridProps> = ({
  data,
  isError,
  noBorder,
  isLoading,
  onSortChange,
  sortOption,
  sortDirection,
  onExportToExcelClicked,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onSortChanged = (event: SortChangedEvent) => {
    if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
      const changedColumn = event.columns.find((col) => col.getSort());
      if (!changedColumn) {
        onSortChange();
        return;
      }
      const changedColumnId = changedColumn.getColId();
      let sortOption: LocationAssetSortOption | undefined = undefined;

      switch (changedColumnId) {
        case ColumnID.ASSET_CODE:
          sortOption = LocationAssetSortOption.ASSET_CODE;
          break;
        case ColumnID.ASSET_TYPE_NAME:
          sortOption = LocationAssetSortOption.ASSET_TYPE_NAME;
          break;
        case ColumnID.DATE_ARRIVED_AT_LOCATION:
          sortOption = LocationAssetSortOption.ARRIVED_AT;
          break;
        case ColumnID.STAYTIME:
          sortOption = LocationAssetSortOption.STAY_TIME;
          break;
        default:
          break;
      }

      let sortOrder: PageSortOrder | undefined = PageSortOrder.ASC;

      switch (changedColumn.getSort()) {
        case 'asc':
          sortOrder = PageSortOrder.ASC;
          break;
        case 'desc':
          sortOrder = PageSortOrder.DESC;
          break;
      }

      onSortChange(sortOption, sortOrder);
    }
  };

  const customGridOptions: GridOptions<LocationAssetModel> = {
    ...BASE_GRID_OPTIONS,
    getRowId: (params) => params.data.dto.assetId.toString(),
    getContextMenuItems: (params) => [
      'copy',
      'separator',
      {
        name: t('export_excel'),
        action: () => onExportToExcelClicked(params.api),
      },
    ],
    onGridReady: (event) => {
      // Set initial sort state to grid
      if (sortOption && sortDirection) {
        // Convert sort option to column id
        let columnId: ColumnID | undefined = ColumnID.FIRST_EVENT_DATE;
        switch (sortOption) {
          case LocationAssetSortOption.ARRIVED_AT:
            columnId = ColumnID.DATE_ARRIVED_AT_LOCATION;
            break;
          case LocationAssetSortOption.ASSET_TYPE_NAME:
            columnId = ColumnID.ASSET_TYPE_NAME;
            break;
          case LocationAssetSortOption.ASSET_CODE:
            columnId = ColumnID.ASSET_CODE;
            break;
          case LocationAssetSortOption.STAY_TIME:
            columnId = ColumnID.STAYTIME;
            break;
          default:
            break;
        }

        event.api.applyColumnState({
          state: [
            {
              colId: columnId,
              sort: sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
            },
          ],
          defaultState: { sort: null },
        });
      }
    },

    onSortChanged: onSortChanged,
  };

  const columns: ColDefOrGroup<LocationAssetModel>[] = [
    {
      colId: ColumnID.ASSET_CODE,
      field: 'dto.assetCode',
      headerName: t('asset'),
    },
    {
      colId: ColumnID.ASSET_TYPE_NAME,
      field: 'dto.assetTypeName',
      headerName: t('type'),
    },
    {
      colId: ColumnID.DATE_ARRIVED_AT_LOCATION,
      field: 'dto.dateArrivedAtLocation',
      headerName: t('arrived_at'),
      cellRenderer: RelativeDateCellRenderer,
      flex: 1,
    },
    {
      colId: ColumnID.STAYTIME,
      field: 'dto.dateArrivedAtLocation',
      headerName: t('stay_time'),
      cellRenderer: (params: ICellRendererParams<LocationAssetModel>) => {
        if (!params.value) return '';
        const arrivedAt = dayjs(params.value);
        const stayTime = dayjs().diff(arrivedAt, 'days');
        return t('number_of_days', { day: stayTime });
      },
      flex: 1,
    },
  ];

  return (
    <div className="flex h-full flex-1 grow flex-col">
      {isError ? (
        <ErrorLoadingDataAlert />
      ) : (
        <PoGrid
          noBorder={noBorder}
          isLoading={isLoading}
          colDefs={columns}
          rowData={data}
          gridOptions={customGridOptions}
          disableDefaultGridOptions
        />
      )}
    </div>
  );
};
